import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setValidating(false); setError("No reset token provided."); return; }
    api.get(`/auth/verify-reset-token?token=${token}`)
      .then(({ data }) => { setValid(true); setEmail(data.email || ""); })
      .catch(err => { setError(formatApiError(err.response?.data?.detail) || "Invalid or expired token"); })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    // FOLLOW-UP 3 (1b): the same rule as change_password, worded the same way
    const PW_RULE = "Password must be at least 8 characters and include one uppercase letter, one number and one special character.";
    if (password.length < 8 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)
        || !/[!@#$%^&*()_+\-=\[\]{}|;':",./<>?`~\\]/.test(password)) { setError(PW_RULE); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(data.message || "Password reset successfully.");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  if (validating) return <div className="flex items-center justify-center min-h-screen"><p className="text-zinc-500 text-sm">Validating reset token...</p></div>;

  return (
    <div className="flex items-center justify-center min-h-screen bg-zinc-50" data-testid="reset-password-page">
      <div className="w-full max-w-sm border border-zinc-200 bg-white p-6 rounded-sm">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mb-1">Reset Password</h1>
        {email && <p className="text-xs text-zinc-500 mb-4">For: {email}</p>}

        {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm" data-testid="reset-error">{error}</div>}
        {success && (
          <div className="mb-3 p-2 bg-green-50 border border-green-200 text-green-700 text-xs rounded-sm" data-testid="reset-success">
            {success} Redirecting to login...
          </div>
        )}

        {valid && !success && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label className="text-xs">New Password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1 rounded-sm" data-testid="reset-new-pw" placeholder="Min 8 chars, 1 capital, 1 number, 1 special" />
              {/* FOLLOW-UP 4 (item 4): the rule is shown before typing, not only after a refusal */}
              <p className="text-[10px] text-zinc-500 mt-1" data-testid="reset-pw-rule">
                Password must be at least 8 characters and include one uppercase letter, one number and one special character.
              </p>
            </div>
            <div>
              <Label className="text-xs">Confirm New Password</Label>
              <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1 rounded-sm" data-testid="reset-confirm-pw" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-sm text-xs" data-testid="reset-submit">
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        )}

        {!valid && !success && (
          <div className="text-center">
            <p className="text-xs text-zinc-500 mb-3">This reset link is invalid or has expired.</p>
            <Button variant="outline" onClick={() => navigate("/login")} className="rounded-sm text-xs">Back to Login</Button>
          </div>
        )}
      </div>
    </div>
  );
}
